'use client';

import React, { useState } from 'react';

import {
    Align,
    Icon,
    IconName,
    IconSize,
    Popover,
    PopoverDirection,
    RadioTile,
    RadioTiles,
    Row,
    Rows,
    Text,
    View,
} from '@bytel/trilogy-react';

import { TradeInCriteriaResponsesType, TradeInCriteriaType } from '@app-types/trade-in';

export type TradeInQuestionnaireProps = {
    questions: TradeInCriteriaType;
    onResponsesChange: (responses: TradeInCriteriaResponsesType['questions']) => void;
};

export function TradeInQuestionnaire({ questions, onResponsesChange }: Readonly<TradeInQuestionnaireProps>) {
    const [responses, setResponses] = useState<TradeInCriteriaResponsesType['questions']>([]);

    const handleResponseChange = (question: TradeInCriteriaType[number], responseId: string) => {
        const next = [...responses];
        const answer = {
            id: question.id,
            reponse: { id: responseId, value: question.reponses.find((reponse) => reponse.id === responseId)!.label },
            question: question.label,
        };

        const index = next.findIndex((response) => response.id === question.id);
        if (index > -1) {
            next[index] = answer;
        } else {
            next.push(answer);
        }

        setResponses(next);
        onResponsesChange(next);
    };

    const isChecked = (questionId: string, responseId: string): boolean =>
        responses.find((response) => response.id === questionId)?.reponse.id === responseId;

    return (
        <Rows>
            {questions.map((question) => (
                <Row key={question.id}>
                    <View flexable align={Align.START} className='gap-medium'>
                        <Text>{question.label}</Text>
                        {question.reponses[0] && (
                            <Popover
                                trigger={<Icon name={IconName.INFOS_CIRCLE} size={IconSize.SMALLER} />}
                                direction={PopoverDirection.LEFT}
                            >
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: question.reponses[0].description.join('<br><br>'),
                                    }}
                                />
                            </Popover>
                        )}
                    </View>
                    <RadioTiles className='grid-radio-list'>
                        {question.reponses.map((reponse) => (
                            <RadioTile
                                horizontal
                                label={reponse.label}
                                checked={isChecked(question.id, reponse.id)}
                                onChange={({ radioChecked }) =>
                                    radioChecked && handleResponseChange(question, reponse.id)
                                }
                                key={reponse.id}
                                id={reponse.id}
                            />
                        ))}
                    </RadioTiles>
                </Row>
            ))}
        </Rows>
    );
}
